import { api, isNotFound } from "@/lib/api-client";
import type { ResendResults } from "@/components/CallHistory/webhook-status";
import type { WebhookLogsResult } from "@/hooks/use-webhook-event-logs";
import { dateRangeFilter, numberRangeFilter } from "@/config/callHistoryFilters";

export interface CallLog {
  id: string;
  workspace_id: string;
  phone_number_id: string | null;
  agent_id: string | null;
  /** Joined in by the `call` function, so no second request resolves it.
   * Null when the call names no agent, or names one since deleted. */
  agent_name: string | null;
  call_sid: string | null;
  call_type: 'inbound' | 'outbound' | 'web';
  from_number: string;
  to_number: string;
  status: string;
  duration: number | null;
  recording_url: string | null;
  recording_sid: string | null;
  transcript: string | null;
  enhanced_transcript: string | null;
  transcript_json: { conversation: Array<any> } | null;
  analysis_data: any | null;
  dynamic_variables: Record<string, unknown> | null;
  summary: string | null;
  cost: number | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  disconnection_reason: string | null;
  transcript_fix_count: number;
  created_at: string;
  updated_at: string;
}

export interface CallLogFilters {
  callTypes?: string[];
  agentIds?: string[];
  campaignIds?: string[];
  fromNumber?: string;
  fromNumbers?: string[];
  toSearch?: string;
  callIdSearch?: string;
  timeRanges?: string[];
  durationRanges?: string[];
  costRanges?: string[];
  disconnectionReasons?: string[];
}

export interface CallListParams {
  workspaceId: string;
  page?: number;
  pageSize?: number;
  filters?: CallLogFilters;
}

export interface CallListResult {
  data: CallLog[];
  count: number;
  totalPages: number;
}

// Decode a numberRangeFilter value into inclusive [min, max] bounds.
// (The "gt"/"lt" ops are applied as >=/<= server-side; the boundary-value
// difference is immaterial for cost/duration filtering.)
function decodeRange(
  encoded: string | undefined,
  scale: number,
): { min?: number; max?: number } {
  if (!encoded) return {};
  const { op, v1, v2 } = numberRangeFilter.decode(encoded);
  const n1 = parseFloat(v1) * scale;
  const n2 = parseFloat(v2) * scale;
  if (isNaN(n1)) return {};
  if (op === "gt") return { min: n1 };
  if (op === "lt") return { max: n1 };
  if (op === "between" && !isNaN(n2)) return { min: n1, max: n2 };
  return {};
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const BASE = "/call";

// Call History list: one page from the `call` edge function (POST /call/list),
// which authorizes the workspace once and joins in each call's agent name.
export async function getCallList({
  workspaceId,
  page = 1,
  pageSize = 100,
  filters,
}: CallListParams): Promise<CallListResult> {
  // Local day boundaries, sent as instants.
  let createdAfter: string | undefined;
  let createdBefore: string | undefined;
  if (filters?.timeRanges && filters.timeRanges.length > 0) {
    const { from, to } = dateRangeFilter.decode(filters.timeRanges[0]);
    if (from) createdAfter = new Date(from + "T00:00:00").toISOString();
    if (to) {
      const end = new Date(to + "T00:00:00");
      end.setHours(23, 59, 59, 999);
      createdBefore = end.toISOString();
    }
  }

  const duration = decodeRange(filters?.durationRanges?.[0], 60);   // minutes -> seconds
  const cost = decodeRange(filters?.costRanges?.[0], 100);          // dollars -> cents

  const callIdRaw = filters?.callIdSearch?.trim();
  const fromNumbers = filters?.fromNumbers?.length
    ? filters.fromNumbers
    : filters?.fromNumber
      ? [filters.fromNumber]
      : undefined;

  const { rows, total } = await api.postPage<CallLog>(`${BASE}/list`, { page, size: pageSize, search: "" }, {
    workspace_id: workspaceId,
    call_types: filters?.callTypes,
    agent_ids: filters?.agentIds,
    campaign_ids: filters?.campaignIds,
    from_numbers: fromNumbers,
    to_number: filters?.toSearch?.trim() || undefined,
    call_id: callIdRaw && UUID_RE.test(callIdRaw) ? callIdRaw : undefined,
    created_after: createdAfter,
    created_before: createdBefore,
    duration_min: duration.min,
    duration_max: duration.max,
    cost_min: cost.min,
    cost_max: cost.max,
    disconnection_reasons: filters?.disconnectionReasons,
  });
  return { data: rows, count: total, totalPages: total ? Math.ceil(total / pageSize) : 0 };
}

/** Cap on one export — enough for any real workspace, low enough to not wedge the tab. */
export const CALL_EXPORT_MAX_ROWS = 50000;

/** Raised when the user cancels an export that is still fetching. */
export class ExportCancelledError extends Error {
  constructor() {
    super("Export cancelled");
    this.name = "ExportCancelledError";
  }
}

/** How far along a running export is, for the dialog's progress line. */
export interface ExportProgress {
  stage: "calls" | "details";
  done: number;
  total: number;
}

export interface ExportFetchOptions {
  signal?: AbortSignal;
  onProgress?: (progress: ExportProgress) => void;
}

const throwIfCancelled = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new ExportCancelledError();
};

/**
 * One request, retried through the transient failures a long export will meet.
 * An export is dozens to hundreds of round trips; without this a single 500 or
 * dropped connection throws away everything already fetched.
 */
async function withRetry<T>(run: () => Promise<T>, signal?: AbortSignal, attempts = 3): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    throwIfCancelled(signal);
    try {
      return await run();
    } catch (error) {
      if (attempt >= attempts) throw error;
      await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
    }
  }
}

/**
 * Every call matching the given filters, for the Call History export. The list
 * only serves pages, so this walks them at a page size large enough to keep
 * the per-page count(*) from dominating. Rows shift down as new calls land
 * mid-walk, so ids are de-duplicated rather than trusted to be disjoint.
 */
export async function fetchAllCallLogs(
  workspaceId: string,
  filters?: CallLogFilters,
  options: ExportFetchOptions & { maxRows?: number } = {},
): Promise<CallLog[]> {
  const { signal, onProgress, maxRows = CALL_EXPORT_MAX_ROWS } = options;
  const PAGE_SIZE = 1000;
  const all: CallLog[] = [];
  const seen = new Set<string>();

  for (let page = 1; ; page++) {
    const { data, count } = await withRetry(
      () => getCallList({ workspaceId, page, pageSize: PAGE_SIZE, filters }),
      signal,
    );
    for (const row of data) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      all.push(row);
    }
    onProgress?.({ stage: "calls", done: Math.min(all.length, maxRows), total: Math.min(count, maxRows) });
    if (data.length < PAGE_SIZE || page * PAGE_SIZE >= count || all.length >= maxRows) break;
  }

  return all.slice(0, maxRows);
}

/** Columns the list RPC leaves out — heavy enough that the table never asks for them. */
export type CallExtraColumn =
  | "recording_url"
  | "summary"
  | "transcript"
  | "enhanced_transcript"
  | "transcript_json"
  | "transcript_fix_count"
  | "analysis_data"
  | "dynamic_variables";

/**
 * The given columns for the given calls, keyed by call id, for exports that
 * pick a field the list doesn't return (POST /call/extras, at most 100 ids a
 * request), a few batches at a time so a large export doesn't turn into a long
 * serial crawl.
 */
export async function fetchCallExtras(
  workspaceId: string,
  ids: string[],
  columns: CallExtraColumn[],
  options: ExportFetchOptions = {},
): Promise<Map<string, Partial<CallLog>>> {
  const { signal, onProgress } = options;
  const byId = new Map<string, Partial<CallLog>>();
  if (ids.length === 0 || columns.length === 0) return byId;

  // Transcript/JSON columns run to tens of KB per row, so ask for fewer of
  // those at a time.
  const BULKY: CallExtraColumn[] = [
    "transcript",
    "enhanced_transcript",
    "transcript_json",
    "analysis_data",
  ];
  const BATCH_SIZE = columns.some((c) => BULKY.includes(c)) ? 50 : 100;
  const CONCURRENCY = 4;

  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) batches.push(ids.slice(i, i + BATCH_SIZE));

  const fetchBatch = (batch: string[]) =>
    withRetry(
      () => api.post<Array<Partial<CallLog> & { id: string }>>(`${BASE}/extras`, {
        workspace_id: workspaceId,
        ids: batch,
        columns,
      }),
      signal,
    );

  for (let i = 0; i < batches.length; i += CONCURRENCY) {
    throwIfCancelled(signal);
    const results = await Promise.all(batches.slice(i, i + CONCURRENCY).map(fetchBatch));
    for (const rows of results) {
      for (const row of rows) byId.set(row.id, row);
    }
    onProgress?.({ stage: "details", done: byId.size, total: ids.length });
  }

  return byId;
}

export interface WorkspaceConcurrency {
  active_calls: number;       // live calls in flight right now
  concurrency_limit: number;  // server-enforced cap (0 = unset)
}

// Live concurrency snapshot for the dashboard tile: active calls vs the
// server-enforced cap (workspaces.call_concurrency_limit), both from one read so
// the denominator always matches what try_reserve_call_slot enforces.
export const getWorkspaceConcurrency = (workspaceId: string) =>
  api.getItem<WorkspaceConcurrency>(`${BASE}/concurrency`, { query: { workspace_id: workspaceId } });

// The whole call — all heavy text/jsonb columns the detail view needs. Null once deleted.
export async function getCallDetails(id: string): Promise<CallLog | null> {
  try {
    return await api.getItem<CallLog>(`${BASE}/${id}`);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/** The call's tool runs, oldest first. */
export const getToolExecutions = <T>(id: string) => api.getItem<T[]>(`${BASE}/${id}/tool-executions`);

/** Re-runs the transcript clean-up for the call. */
export const fixTranscript = (id: string) =>
  api.post<{ enhanced_transcript: string | null }>(`${BASE}/${id}/transcript`, {});

/** The call's webhook delivery log, redacted to the caller's role. */
export const getWebhookLogs = (id: string) => api.getItem<WebhookLogsResult>(`${BASE}/${id}/webhook-logs`);

/** Sends the given events again; answers each one's result. */
export const resendWebhooks = async (id: string, events: string[]): Promise<ResendResults> =>
  (await api.post<{ results: ResendResults }>(`${BASE}/${id}/webhooks/resend`, { events })).results;

/** A link that plays the call's Twilio recording until `expires_at`, an hour after it was asked for. */
export interface RecordingLink {
  url: string;
  expires_at: string;
}

/** Asked of twilio-recording-proxy with the session: an <audio src> cannot send one, so the link carries its own permission. */
export const getRecordingLink = (id: string) => api.getItem<RecordingLink>(`/twilio-recording-proxy/${id}/link`);
